import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";

const STAKES = [100, 500, 1000, 5000];

export default function BetSlip({ pick, onClear, onPlaced }) {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [stake, setStake] = useState("");
  const [busy, setBusy] = useState(false);
  const s = Number(stake) || 0;
  const isBack = pick?.side === "back";
  const risk = pick ? (isBack ? s : s * (pick.price - 1)) : 0;
  const win = pick ? (isBack ? s * (pick.price - 1) : s) : 0;

  const place = async () => {
    if (!user) return nav("/login");
    if (user.role !== "client") return toast.error("Only client accounts can place bets");
    if (s <= 0) return toast.error("Enter a stake");
    setBusy(true);
    try {
      const { data } = await api.post("/bets", {
        match_id: pick.matchId,
        selection: pick.selection,
        side: pick.side,
        stake: s,
      });
      toast.success(`Bet placed · ${data.credits.toFixed(2)} credits left`);
      await refreshUser();
      onPlaced?.(data.bet);
      onClear();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-zinc-950 border border-amber-500/20 rounded-md overflow-hidden" data-testid="bet-slip">
      <div className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-600 text-black px-4 py-2 font-display uppercase tracking-widest font-bold text-sm flex justify-between">
        <span>Bet Slip</span>
        {user?.role === "client" && (
          <span className="font-mono-stat normal-case tracking-normal" data-testid="bet-slip-balance">
            {(user.credits || 0).toFixed(2)} cr
          </span>
        )}
      </div>
      {!pick ? (
        <div className="p-5 text-sm text-zinc-500" data-testid="bet-slip-empty">
          Click on any odds to add a selection.
        </div>
      ) : (
        <div className={`p-4 space-y-3 ${isBack ? "bg-[#72BBEF]/10" : "bg-[#FAA9BA]/10"}`}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-[11px] text-zinc-400">{pick.match}</div>
              <div className="font-semibold" data-testid="bet-slip-selection">{pick.selection}</div>
              <div className="text-xs uppercase font-bold mt-0.5" style={{ color: isBack ? "#72BBEF" : "#FAA9BA" }}>
                {pick.side} @ <span className="font-mono-stat">{pick.price?.toFixed(2)}</span>
              </div>
            </div>
            <button onClick={onClear} data-testid="bet-slip-clear" className="text-zinc-400 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          </div>
          <Input
            type="number"
            min="0"
            placeholder="Stake"
            value={stake}
            onChange={(e) => setStake(e.target.value)}
            className="bg-black border-amber-500/20"
            data-testid="bet-slip-stake"
          />
          <div className="grid grid-cols-4 gap-1">
            {STAKES.map((v) => (
              <button key={v} onClick={() => setStake(String(v))} className="text-xs bg-zinc-800 hover:bg-zinc-700 rounded py-1.5 font-mono-stat" data-testid={`stake-${v}`}>
                {v}
              </button>
            ))}
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-zinc-400">{isBack ? "Potential return" : "Liability"}</span>
            <span className="font-mono-stat text-amber-300" data-testid="bet-slip-return">
              {isBack ? (s * pick.price).toFixed(2) : risk.toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between text-xs text-zinc-500">
            <span>Profit if wins</span>
            <span className="font-mono-stat text-emerald-300">{win.toFixed(2)}</span>
          </div>
          <Button
            data-testid="bet-slip-place"
            disabled={busy}
            onClick={place}
            className="w-full bg-gradient-to-r from-amber-300 via-amber-400 to-amber-600 text-black font-display uppercase tracking-widest"
          >
            {!user ? "Login to Place" : busy ? "Placing…" : "Place Bet"}
          </Button>
          <div className="text-[10px] text-zinc-500 text-center">Virtual credits only · non-redeemable</div>
        </div>
      )}
    </div>
  );
}
